import type { DirectCallInput } from './direct-call-input';
import {
  HandsFreeVoiceActivity,
  isNoiseFragment,
  isNonSpeechTranscript,
} from './direct-call-voice-activity';

interface QueuedTurn {
  text: string;
  generation: number;
}

export interface DirectCallHandsFreeOptions {
  // False while another sender (a typed send) is in flight; queued turns wait.
  canSend?: () => boolean;
  // True while reply audio is waiting on the open recording to release.
  yieldToReply?: () => boolean;
  // Bug 35526: whether a turn that finished recording is still wanted (the
  // call is live). Defaults to `canListen`. Releasing the microphone resumes a
  // held reply, so `canListen` (which is false while a reply speaks) is the
  // wrong test here: it parked the spoken turn in the composer instead of
  // sending it.
  turnStillWanted?: () => boolean;
  // Story 34588: true only while the shared audio session has native voice
  // processing (echo cancellation) active, so an open microphone during
  // playback is hearing Tim, not the speaker's own reply. False (the
  // default) keeps every interaction below identical to Story 34580: no
  // interruption call is ever made.
  voiceProcessingActive?: () => boolean;
  // True while reply audio is actually playing (not merely queued).
  replySpeaking?: () => boolean;
  // Stop the reply and suppress the rest of that turn's audio. Called at
  // most once per recording, on the first VAD speech sample captured while
  // `replySpeaking()` is true and `voiceProcessingActive()` is true.
  bargeIn?: () => void;
  // Bug 35397: close a turn this long after the last syllable onset even if
  // noise keeps the level above the threshold. Default
  // DEFAULT_NO_NEW_SPEECH_MS.
  noNewSpeechMs?: number;
}

// Turn taking. The caller supplies playback/draft/delivery gating. Voice
// activity delimits recordings; Whisper remains the transcription engine.
// Each finished turn is queued and sent in order, so capture continues while a
// send or a seat reply is still pending.
// 2 s of 125 ms samples: the loud ambient that must precede a loud onset. A
// shorter run is a held filler ("ummm") as often as a fan (PR 4960 review).
const AMBIENT_SAMPLES = 16;
// US 35617 AC6: a quiet gap this long ends the turn. 1.2 s cut Tim off at a
// breath mid-sentence; 2 s matches the pause other call apps allow.
export const HANDS_FREE_SILENCE_MS = 2000;
// Bug 35397's noisy-room close (no new syllable onset for this long). Never
// shorter than the silence window, so a loud room does not cut a pause that a
// quiet room would keep.
export const DEFAULT_NO_NEW_SPEECH_MS = HANDS_FREE_SILENCE_MS;

export class DirectCallHandsFree {
  enabled = false;
  private busy = false;
  private generation = 0;
  private openedAt = 0;
  private lastSoundAt = 0;
  private soundSamples = 0;
  private lastOnsetAt = 0;
  private loudOnsets = 0;
  // Onsets are counted once per burst: a step stays an onset for up to three
  // samples while the low before it is still in the window.
  private inOnset = false;
  // Loud onsets count only once a loud ambient was heard first: at least 2 s
  // of sound with no onset (a fan already running) right before a loud onset.
  // A loud close talker in a quiet room never arms it (PR 4960 review). The
  // state spans recordings (one reopens after every turn) and resets only on
  // a sample that is not sound: while the room stays loud it stays armed.
  private ambientRun = 0;
  private ambientArmed = false;
  private readonly voice = new HandsFreeVoiceActivity();
  private readonly outbox: QueuedTurn[] = [];
  private draining: Promise<boolean> | null = null;
  private readonly canSend: () => boolean;
  private readonly yieldToReply: () => boolean;
  private readonly turnStillWanted: () => boolean;
  private readonly voiceProcessingActive: () => boolean;
  private readonly replySpeaking: () => boolean;
  private readonly bargeIn: () => void;
  private readonly noNewSpeechMs: number;
  private bargedInThisRecording = false;
  // US 35617 AC2: muted with the microphone held open and paused.
  private holding = false;

  constructor(
    private readonly input: DirectCallInput,
    private readonly canListen: () => boolean,
    private readonly send: (text: string) => Promise<boolean>,
    private readonly settled: () => void = () => {},
    options: DirectCallHandsFreeOptions = {}
  ) {
    this.canSend = options.canSend ?? (() => true);
    this.yieldToReply = options.yieldToReply ?? (() => false);
    this.turnStillWanted = options.turnStillWanted ?? canListen;
    this.voiceProcessingActive = options.voiceProcessingActive ?? (() => false);
    this.replySpeaking = options.replySpeaking ?? (() => false);
    this.bargeIn = options.bargeIn ?? (() => {});
    this.noNewSpeechMs = options.noNewSpeechMs ?? DEFAULT_NO_NEW_SPEECH_MS;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.holding = false;
    this.generation++;
    this.soundSamples = 0;
    this.bargedInThisRecording = false;
    this.input.cancel();
    if (enabled) this.input.failure = null;
  }

  /**
   * Bug 35673: a replay or a video takes the mic. Like setEnabled(false), and
   * the turn it cuts short settles: setEnabled bumps the generation, so the
   * cancelled turn never would, and the caller's explicit Speak Now turn would
   * stay open, ignoring reply audio once the mic comes back.
   */
  pauseForPlayback(): void {
    this.setEnabled(false);
    this.settled();
  }

  /**
   * US 35617 AC2: false mute. Muting keeps the microphone open but paused, so
   * unmuting resumes it at once instead of reopening capture, and nothing
   * heard while muted is kept. The recording open at mute is discarded, as
   * mute always did, and a fresh one is held from the next tick. With an
   * adapter that cannot pause this is setEnabled.
   */
  setMuted(muted: boolean, now = Date.now()): void {
    if (!muted) {
      if (!this.holding || !this.input.held || !this.input.hold(false)) {
        this.setEnabled(true);
        return;
      }
      this.enabled = true;
      this.holding = false;
      this.generation++;
      this.input.failure = null;
      this.beginTurn(now);
      return;
    }
    if (!this.enabled) return;
    this.enabled = false;
    this.generation++;
    this.soundSamples = 0;
    this.bargedInThisRecording = false;
    // The open recording is always released, and keepHeld opens a fresh one
    // and pauses it on the next tick, so a held file never carries audio from
    // before the mute (PR 5310 review). Without canPause nothing is held.
    this.holding = this.input.canHold;
    this.input.cancel();
  }

  // US 35617 AC2: while muted, keep one paused recording open, released
  // whenever listening is not allowed or reply audio waits on the microphone.
  private async keepHeld(): Promise<void> {
    if (this.busy) return;
    const state = this.input.state;
    if (!this.canListen() || this.yieldToReply()) {
      if (state === 'recording' || state === 'starting') this.input.cancel();
      return;
    }
    if (state !== 'idle' || this.input.failure) return;
    const generation = this.generation;
    this.busy = true;
    try {
      const started = await this.input.start();
      if (!started) {
        // Unmuted (or muted again) meanwhile: that change owns the state now.
        if (generation !== this.generation) return;
        // A failed hold is not the user's failure: stop holding, and a real
        // unmute reports it.
        this.input.failure = null;
        this.holding = false;
        return;
      }
      // Unmuted or turned off while opening: this recording began muted.
      if (generation !== this.generation || !this.holding) {
        this.input.cancel();
        return;
      }
      if (!this.input.hold(true)) {
        this.input.cancel();
        this.holding = false;
      }
    } finally {
      this.busy = false;
    }
  }

  // A fresh turn: the recording just opened or resumed.
  private beginTurn(now: number): void {
    this.openedAt = now;
    this.lastSoundAt = now;
    this.lastOnsetAt = now;
    this.soundSamples = 0;
    this.loudOnsets = 0;
    this.inOnset = false;
    this.bargedInThisRecording = false;
  }

  async finishNow(): Promise<void> {
    if (this.input.state === 'recording') await this.tick(Date.now(), true);
  }

  async tick(now: number, explicitDone = false): Promise<void> {
    // Queued turns are already spoken; they send even while muted.
    if (this.outbox.length && !this.draining && this.canSend())
      await this.drain();
    if (!this.enabled && this.holding) return this.keepHeld();
    if (!this.enabled || this.busy) return;
    if (!this.canListen()) {
      if (this.input.state === 'recording' || this.input.state === 'starting')
        this.input.cancel();
      this.soundSamples = 0;
      return;
    }
    const generation = this.generation;
    let queued: { delivered: Promise<void> } | null = null;
    this.busy = true;
    try {
      if (this.input.state === 'idle') {
        if (this.yieldToReply()) return;
        if (this.input.failure) {
          this.setEnabled(false);
          return;
        }
        const started = await this.input.start();
        if (generation !== this.generation || !this.enabled) return;
        if (!started) {
          this.setEnabled(false);
          return;
        }
        this.beginTurn(now);
        return;
      }
      if (this.input.state !== 'recording') return;
      const wasCalibrated = this.voice.calibrated;
      const level = this.input.inputLevel;
      const heard = this.voice.observe(now, level);
      if (heard) {
        this.lastSoundAt = now;
        this.soundSamples++;
        if (this.voice.onset) {
          this.lastOnsetAt = now;
          if (!this.inOnset && this.voice.loudOnset) {
            if (this.ambientRun >= AMBIENT_SAMPLES) this.ambientArmed = true;
            if (this.ambientArmed) this.loudOnsets++;
          }
          this.ambientRun = 0;
        } else this.ambientRun++;
        // Native barge-in (Story 34588): the first captured speech sample
        // while a reply is actually playing under active voice processing
        // stops it. Without voice processing this never fires, so listening
        // while `speaking` (gated upstream by the caller's `canListen`)
        // cannot happen in the first place.
        if (
          !this.bargedInThisRecording &&
          this.voiceProcessingActive() &&
          this.replySpeaking()
        ) {
          this.bargedInThisRecording = true;
          this.bargeIn();
        }
      } else if (
        !wasCalibrated &&
        this.voice.calibrated &&
        this.voice.threshold > HandsFreeVoiceActivity.baseThreshold
      ) {
        // Loud ambient counted as sound before the floor was known: discard it.
        this.ambientRun = 0;
        this.soundSamples = 0;
        this.lastSoundAt = now;
      }
      if (!heard) {
        this.ambientRun = 0;
        this.ambientArmed = false;
      }
      this.inOnset = this.voice.onset;
      const silence = now - this.lastSoundAt >= HANDS_FREE_SILENCE_MS;
      const limit = now - this.openedAt >= 45000;
      // Bug 35397: speech over a loud ambient (two onsets rising from dips
      // above the threshold) followed by held sound with no new onset for
      // noNewSpeechMs is noise the floor has not caught up with: end the turn.
      // Anything else (quiet-room speech, a soft word, noise alone) keeps the
      // silence rule and is always transcribed; when in doubt, send (PR 4960
      // review). The transcript filters drop what noise produces.
      const stalled =
        !silence &&
        this.soundSamples >= 3 &&
        this.loudOnsets >= 2 &&
        now - this.lastOnsetAt >= this.noNewSpeechMs;
      // Against a raised floor, isolated ambient spikes must not add up to a
      // turn: a quiet gap resets a count that never reached speech.
      if (
        silence &&
        this.soundSamples < 3 &&
        this.voice.threshold > HandsFreeVoiceActivity.baseThreshold
      )
        this.soundSamples = 0;
      if (this.yieldToReply() && !explicitDone) {
        // Reply audio is waiting. Release a recording with nothing in it; a
        // recording with any captured sound finishes as a turn once quiet.
        if (this.soundSamples === 0) {
          this.input.cancel();
          return;
        }
        if (silence || limit || stalled)
          queued = await this.finishTurn(generation, false);
        return;
      }
      if (!explicitDone && !silence && !limit && !stalled) return;
      if (!explicitDone && this.soundSamples < 3) {
        if (now - this.openedAt >= 15000) this.input.cancel();
        return;
      }
      queued = await this.finishTurn(generation, explicitDone);
    } finally {
      this.busy = false;
    }
    // Wait for delivery outside the capture lock so the next turn can record.
    if (queued) await queued.delivered;
  }

  // Transcribe and queue the turn. Resolves to the delivery that settles it,
  // boxed so awaiting the transcription never also awaits the send.
  private async finishTurn(
    generation: number,
    explicit: boolean
  ): Promise<{ delivered: Promise<void> } | null> {
    const transcribed = await this.input.finish();
    const current =
      this.enabled && generation === this.generation && this.turnStillWanted();
    if (current && !transcribed) {
      this.setEnabled(false);
      this.settled();
      return null;
    }
    if (!current) {
      if (generation === this.generation) this.settled();
      return null;
    }
    const text = this.input.draft.trim();
    // A turn the user ended with Done is sent even if short; an automatic
    // turn that is one letter or digit is noise (Bug 35397).
    if (isNonSpeechTranscript(text) || (!explicit && isNoiseFragment(text))) {
      if (this.input.draft.trim() === text) this.input.setDraft('');
      this.settled();
      return null;
    }
    this.outbox.push({ text, generation });
    if (this.input.draft.trim() === text) this.input.setDraft('');
    const delivered = this.canSend() ? this.drain() : Promise.resolve(false);
    // A rejection already settled inside deliver(); settle once, not twice.
    return {
      delivered: delivered.then(
        (settledOnRejection) => {
          if (!settledOnRejection) this.settled();
        },
        (error: unknown) => {
          this.settled();
          throw error;
        }
      ),
    };
  }

  private drain(): Promise<boolean> {
    if (!this.draining)
      this.draining = this.deliver().finally(() => {
        this.draining = null;
      });
    return this.draining;
  }

  // Resolves true when a rejected turn was restored to the draft and settled.
  private async deliver(): Promise<boolean> {
    while (this.outbox.length && this.canSend()) {
      const turn = this.outbox[0];
      const accepted = await this.send(turn.text);
      if (accepted) {
        this.outbox.shift();
        continue;
      }
      // Uncertain delivery: never retry. Keep it and everything queued after
      // it in the draft, ahead of any text typed while they were pending.
      const unsent = this.outbox.splice(0).map((queued) => queued.text);
      const typed = this.input.draft.trim();
      this.input.setDraft([...unsent, typed].filter(Boolean).join(' '));
      if (this.enabled && turn.generation === this.generation)
        this.setEnabled(false);
      // A deferred drain runs after finishTurn already settled, so tell the
      // screen again or the restored draft never reaches the composer.
      this.settled();
      return true;
    }
    return false;
  }
}
